import { useState, useEffect, useMemo } from 'react';
import apiClient from '../api/apiClient';
import { useBu } from '../components/layout/Header';

/**
 * Returns the selected BU's ST business unit IDs for filtering ST data client-side.
 * Also returns BU name and list of all units.
 */
export function useBuFilter() {
  const { selectedBu } = useBu();
  const [units, setUnits] = useState([]);

  useEffect(() => {
    apiClient.get('/business-units')
      .then(res => setUnits(res.data.units || []))
      .catch(() => {});
  }, []);

  const selectedUnit = useMemo(() => {
    if (!selectedBu) return null;
    return units.find(u => u.id === selectedBu) || null;
  }, [selectedBu, units]);

  const selectedBuName = selectedUnit ? selectedUnit.name : null;
  const selectedStBuId = selectedUnit ? selectedUnit.st_business_unit_id : null;

  // Get all ST BU IDs for the selected local BU (usually just one)
  const stBuIds = useMemo(() => {
    if (!selectedStBuId) return null; // null means no filter
    return [selectedStBuId];
  }, [selectedStBuId]);

  // Filter function for ST jobs (have businessUnitId)
  const filterJob = useMemo(() => {
    if (!stBuIds) return () => true;
    return (job) => stBuIds.includes(job.businessUnitId);
  }, [stBuIds]);

  // Filter function for ST invoices (may have businessUnitId, businessUnit.id, or businessUnitName)
  const filterInvoice = useMemo(() => {
    if (!selectedBuName && !stBuIds) return () => true;
    return (inv) => {
      const buId = inv.businessUnitId || inv.businessUnit?.id;
      if (buId && stBuIds) return stBuIds.includes(buId);
      const buName = inv.businessUnitName || inv.businessUnit?.name;
      if (buName && selectedBuName) return buName.toLowerCase().includes(selectedBuName.toLowerCase());
      // If no BU info on the invoice, include it (don't filter out)
      return !stBuIds;
    };
  }, [stBuIds, selectedBuName]);

  return {
    selectedBu,
    selectedBuName,
    selectedStBuId,
    stBuIds,
    units,
    filterJob,
    filterInvoice,
  };
}
